import { useMutation, useQuery } from "@apollo/client/react";
import EDIT_USER from "@graphql/mutations/EditUser";
import { useCurrentUser } from "@hooks/use-current-user";
import { useNoteUsage } from "@hooks/use-note-usage";
import {
  usePremiumFeature,
  UserPermissions,
} from "@hooks/use-premium-feature";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useEffect, useEffectEvent, useRef, useState } from "react";

import {
  type MilestoneConfig,
  getMilestonePrompts,
  getMilestones,
} from "../components/onboarding-milestones/milestoneConfig";
import ONBOARDING_PROGRESS_QUERY from "../graphql/queries/OnboardingProgress";

interface MilestoneStatus {
  key: string;
  label: string;
  icon: MilestoneConfig["icon"];
  completed: boolean;
  current: boolean;
  showMeHow: string | null;
}

interface OnboardingMilestones {
  first_note_recorded?: boolean;
  template_created?: boolean;
  three_day_notes?: boolean;
  note_approved?: boolean;
  note_exported?: boolean;
}

export function useOnboardingMilestones() {
  const { user, refetch: refetchCurrentUser } = useCurrentUser();
  const { totalNotesCreated } = useNoteUsage();
  const { hasFeatureAccess } = usePremiumFeature();
  const [editUser] = useMutation(EDIT_USER);
  const { tryWebMilestoneOrderSwap, releaseWebOnboardingMilestones } =
    useFlags();
  const enabled = !!releaseWebOnboardingMilestones;
  const swapOrder = !!tryWebMilestoneOrderSwap;

  // Users without template access (e.g. expired free plan) cannot create
  // templates — exclude that milestone so it doesn't block completion.
  const canCreateTemplates = hasFeatureAccess(
    UserPermissions.ADAPTIVE_TEMPLATES_ENABLED,
  );

  const orderedMilestones = getMilestones(
    swapOrder,
    canCreateTemplates,
  );
  const orderedPrompts = getMilestonePrompts(
    swapOrder,
    canCreateTemplates,
  );

  // Register A/B variant so all onboarding events include it
  useEffect(() => {
    if (!enabled) return;
    mixpanel.register({
      onboarding_milestone_order: swapOrder
        ? "note_first"
        : "template_first",
    });
  }, [swapOrder, enabled]);

  const {
    data: onboardingData,
    loading: onboardingLoading,
    refetch: refetchOnboarding,
  } = useQuery(ONBOARDING_PROGRESS_QUERY, {
    fetchPolicy: "cache-and-network",
    skip: !enabled,
  });

  const progress = onboardingData?.onboardingProgress;
  const savedMilestones: OnboardingMilestones =
    (progress?.onboardingMilestones as OnboardingMilestones) ?? {};

  const distinctDays = progress?.distinctNoteCreationDays ?? 0;
  const hasApprovedNote = !!progress?.hasApprovedNote;
  const hasExportedNote = !!progress?.hasExportedNote;
  const hasCreatedTemplate = !!progress?.hasCreatedTemplate;
  const noteCount = totalNotesCreated ?? 0;

  // Only auto-detect template_created when template is the first step.
  // In the note-first variant the FTUE runs first and populates sample
  // templates, which would falsely trigger hasCreatedTemplate.
  const templateCreated = swapOrder
    ? !!savedMilestones.template_created
    : hasCreatedTemplate || !!savedMilestones.template_created;

  const completionMap: Record<string, boolean> = {
    first_note_recorded:
      noteCount >= 1 || !!savedMilestones.first_note_recorded,
    template_created: templateCreated,
    note_approved: hasApprovedNote || !!savedMilestones.note_approved,
    note_exported: hasExportedNote || !!savedMilestones.note_exported,
    three_day_notes:
      distinctDays >= 3 || !!savedMilestones.three_day_notes,
  };

  // Apollo mutations don't queue — chain promises to prevent out-of-order writes.
  // pendingRef accumulates all keys set so far (server + local), writeChainRef
  // serializes the actual network calls.
  const pendingRef = useRef<OnboardingMilestones>({
    ...savedMilestones,
  });
  const writeChainRef = useRef<Promise<void>>(Promise.resolve());

  // Sync pendingRef when server data arrives. savedMilestonesJson is a stable
  // primitive so we can depend on it directly without lint suppression.
  const savedMilestonesJson = JSON.stringify(savedMilestones);
  useEffect(() => {
    const parsed: OnboardingMilestones = JSON.parse(
      savedMilestonesJson,
    );
    pendingRef.current = {
      ...pendingRef.current,
      ...parsed,
    };
  }, [savedMilestonesJson]);

  // Enqueue a serialized write of the given keys to the milestone JSON blob.
  // Safe from stale closures: all state is read from refs, not render-time values.
  const enqueueMilestoneWrite = (keys: string[]) => {
    for (const key of keys) {
      pendingRef.current = { ...pendingRef.current, [key]: true };
    }
    const snapshot = { ...pendingRef.current };

    writeChainRef.current = writeChainRef.current
      .then(() =>
        editUser({
          variables: { onboardingMilestones: snapshot },
        }),
      )
      .then(() => {
        refetchOnboarding();
      })
      .catch((e) => {
        console.error("Failed to update milestone", e);
      });
  };

  const completeMilestone = (key: string) => {
    if (!enabled) return;
    enqueueMilestoneWrite([key]);
  };

  // Persist milestones that were auto-detected from live data but not yet saved.
  // This prevents regression if the underlying data changes (e.g. note deleted).
  const persistAutoDetected = useEffectEvent(() => {
    const keysToSave = (
      [
        "first_note_recorded",
        "template_created",
        "note_approved",
        "note_exported",
        "three_day_notes",
      ] as const
    ).filter((key) => completionMap[key] && !savedMilestones[key]);

    if (keysToSave.length > 0) {
      enqueueMilestoneWrite([...keysToSave]);
    }
  });

  const hasLoadedOnboarding = !onboardingLoading || !!progress;

  useEffect(() => {
    if (!enabled || !hasLoadedOnboarding) return; // wait for first load
    persistAutoDetected();
  }, [
    enabled,
    hasLoadedOnboarding,
    completionMap.first_note_recorded,
    completionMap.template_created,
    completionMap.note_approved,
    completionMap.note_exported,
    completionMap.three_day_notes,
  ]);

  const completedCount = orderedMilestones.filter(
    (m) => completionMap[m.key],
  ).length;
  const allComplete = completedCount === orderedMilestones.length;
  const firstIncompleteIndex = orderedMilestones.findIndex(
    (m) => !completionMap[m.key],
  );

  // Track completion: when all milestones are done, mark onboarding complete
  const [justCompleted, setJustCompleted] = useState(false);
  const hasMarkedComplete = useRef(false);

  const onAllComplete = useEffectEvent(() => {
    hasMarkedComplete.current = true;
    setJustCompleted(true);
    mixpanel.track("Onboarding: All Milestones Complete");
    writeChainRef.current = writeChainRef.current
      .then(() =>
        editUser({
          variables: { isOnboardingComplete: true },
        }),
      )
      .then(() => {
        refetchCurrentUser();
      });
  });

  useEffect(() => {
    if (
      enabled &&
      allComplete &&
      !user?.isOnboardingComplete &&
      !hasMarkedComplete.current
    ) {
      onAllComplete();
    }
  }, [enabled, allComplete, user?.isOnboardingComplete]);

  const milestones: MilestoneStatus[] = orderedMilestones.map(
    (m, i) => ({
      key: m.key,
      label: m.label,
      icon: m.icon,
      completed: completionMap[m.key],
      current: i === firstIncompleteIndex,
      showMeHow: m.showMeHow,
    }),
  );

  const prompt =
    firstIncompleteIndex >= 0
      ? orderedPrompts[firstIncompleteIndex]
      : { bold: "Look at you go!", rest: "" };

  return {
    milestones,
    completedCount,
    totalCount: orderedMilestones.length,
    progress: Math.round(
      (completedCount / orderedMilestones.length) * 100,
    ),
    allComplete,
    justCompleted,
    prompt,
    completeMilestone,
    isOnboardingComplete: !!user?.isOnboardingComplete,
  };
}
